import { SurveysActions } from 'store/actions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeedbackModal from 'components/surway/survayModal';
import { SurveysSelectors } from 'store/selectors';
import useAuthProps from '../useAuthProps';
import useModal from '../useModal';
import { useHashParams } from '../useHashParams';

const useUserSurveys = ({ enabled }: { enabled: boolean }) => {
  const { isAuthenticated, currentUser } = useAuthProps();
  const dispatch = useDispatch();
  const [showModal] = useModal(FeedbackModal);
  const { entities: surveys, isFetched } = useSelector(SurveysSelectors.surveysState);
  const [queue, setQueue] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [, setHashParams] = useHashParams();
  const showBannerRef = useRef(showBanner);

  useEffect(() => {
    showBannerRef.current = showBanner;
  }, [showBanner]);

  useEffect(() => {
    if (!enabled) return;
    if (!isAuthenticated) {
      dispatch(SurveysActions.getSurveys.reset());
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dispatch(SurveysActions.getSurveys.request({ params: { userId: currentUser?.id } }));
    }
  }, [dispatch, isAuthenticated, currentUser?.id, enabled]);

  // Fill queue once surveys load
  const [completed, setCompleted] = useState<number[]>([]);

  // Fill queue once surveys load, excluding completed ones
  useEffect(() => {
    if (surveys?.length && isFetched) {
      const remaining = surveys.map((s) => s.id).filter((id) => !completed.includes(id));
      setQueue(remaining);
    }
  }, [surveys, isFetched, completed]);

  const handleSurveyClose = useCallback(
    (surveyId: number) => {
      setCompleted((prev) => [...prev, surveyId]); // mark as done
      setQueue((prev) => {
        const newQueue = prev.filter((id) => id !== surveyId);
        if (newQueue.length === 0 && showBannerRef.current) {
          setHashParams({ surveySuccess: true });
        }

        return newQueue;
      });
      setActiveSurvey(null);
    },
    [setHashParams],
  );

  // Open next survey if none is active
  useEffect(() => {
    if (!activeSurvey && isFetched && queue.length > 0) {
      const nextSurveyId = queue[0];
      if (completed.includes(nextSurveyId)) return; // skip if done

      const survey = surveys.find((s) => s.id === nextSurveyId);
      if (survey) {
        setActiveSurvey(survey);

        showModal({
          onClose: () => handleSurveyClose(survey.id),
          userId: currentUser?.id,
          surveyId: survey.id,
          eventId: survey.event_id,
          rate: survey.rate,
          setSuccess: () => setShowBanner(true),
        });
      }
    }
  }, [
    queue,
    activeSurvey,
    surveys,
    showModal,
    currentUser?.id,
    setShowBanner,
    handleSurveyClose,
    isFetched,
    completed,
  ]);
};

export default useUserSurveys;
